import axios, { AxiosInstance, AxiosError } from 'axios';
import { ApiResponse } from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          // Debug logging
          if (config.url?.includes('/users')) {
            console.log('Sending request to:', config.url);
            console.log('Token present:', !!token);
            console.log('Token length:', token.length);
          }
        } else if (!config.url?.includes('/auth/login')) {
          console.warn('No access token found for request:', config.url);
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        const status = error.response?.status;
        const url = error.config?.url || '';
        
        // Only handle authentication errors, and not for auth endpoints
        if ((status === 401 || status === 422) && 
            !url.includes('/auth/login') &&
            !window.location.pathname.includes('/login')) {
          
          const errorData = error.response?.data as any;
          const errorMessage = (errorData?.message || errorData?.error || '').toLowerCase();
          
          // Check if it's actually an authentication/token error
          const isAuthError = errorMessage.includes('token') || 
                             errorMessage.includes('authorization') ||
                             errorMessage.includes('expired') ||
                             errorMessage.includes('invalid') ||
                             errorMessage.includes('missing') ||
                             errorMessage.includes('unauthorized') ||
                             errorMessage.includes('authentication failed');
          
          if (isAuthError) {
            // Don't redirect or clear token if user just logged in (within last 5 seconds)
            const justLoggedIn = sessionStorage.getItem('just_logged_in');
            if (justLoggedIn) {
              console.warn('Auth error detected but user just logged in, ignoring redirect');
              // Verify token still exists
              const token = localStorage.getItem('access_token');
              if (!token) {
                console.error('Token missing after login - this should not happen');
              }
              // Don't clear token or redirect - just reject the error
              return Promise.reject(error);
            }
            
            // Token expired, invalid, or missing - clear auth and redirect
            localStorage.removeItem('access_token');
            localStorage.removeItem('user');
            // Use setTimeout to avoid redirect during render and prevent multiple redirects
            setTimeout(() => {
              const currentPath = window.location.pathname;
              if (!currentPath.startsWith('/login') && 
                  !currentPath.includes('login') &&
                  localStorage.getItem('access_token') === null) {
                window.location.href = '/login/admin';
              }
            }, 100);
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Generic request methods
  async get<T>(url: string): Promise<ApiResponse<T>> {
    const response = await this.api.get<ApiResponse<T>>(url);
    return response.data;
  }

  async post<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.api.post<ApiResponse<T>>(url, data);
    return response.data;
  }

  async put<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.api.put<ApiResponse<T>>(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<ApiResponse<T>> {
    const response = await this.api.delete<ApiResponse<T>>(url);
    return response.data;
  }
}

export const apiService = new ApiService();

